import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminMe } from "@/lib/api";
import { CORE_PAGES } from "@/data/corePages";
import { LOCATIONS } from "@/data/locations";
import { GROUP_SIZES } from "@/data/groupSizes";
import { INTENTS } from "@/data/intents";
import { AUDIENCES } from "@/data/audiences";
import { FORMATS } from "@/data/formats";
import { COMPARISONS } from "@/data/comparisons";
import { COMMERCIAL } from "@/data/commercial";
import { GUIDES, GUIDES_INDEX } from "@/data/guides";
import { POPTAVKA_PAGE } from "@/pages/Poptavka";
import routes from "@/data/seoRoutes.json";

const ALL = [
  ...Object.values(CORE_PAGES).map((p) => ({ ...p, pageType: p.type || "core", keyword: p.h1 })),
  ...LOCATIONS.map((p) => ({ ...p, pageType: "location", keyword: p.h1 })),
  ...GROUP_SIZES.map((p) => ({ ...p, pageType: "size", keyword: p.h1 })),
  ...INTENTS.map((p) => ({ ...p, pageType: "intent", keyword: p.h1 })),
  ...AUDIENCES.map((p) => ({ ...p, pageType: "audience", keyword: p.h1 })),
  ...FORMATS.map((p) => ({ ...p, pageType: "format", keyword: p.h1 })),
  ...COMPARISONS.map((p) => ({ ...p, pageType: "comparison", keyword: p.h1 })),
  ...COMMERCIAL.map((p) => ({ ...p, pageType: "commercial", keyword: p.h1 })),
  {
    path: "/pruvodce", pageType: "hub", keyword: "průvodce firemním teambuildingem",
    seo: GUIDES_INDEX.seo, h1: GUIDES_INDEX.h1, blocks: [], related: [], breadcrumbs: GUIDES_INDEX.breadcrumbs,
  },
  { ...POPTAVKA_PAGE, pageType: "conversion", keyword: "poptávka teambuildingu", h1: "Připravíme vám misi", blocks: [], related: [] },
  ...GUIDES.map((g) => ({
    path: g.path, pageType: "guide", keyword: g.h1,
    seo: { title: g.title, description: g.description, schema: ["Article", "BreadcrumbList"] },
    h1: g.h1,
    blocks: [{ t: "prose", sections: g.sections }],
    related: g.related, breadcrumbs: g.breadcrumbs,
  })),
];

const collectText = (blocks) => {
  let out = [];
  for (const b of blocks || []) {
    if (b.title) out.push(b.title);
    if (b.sub) out.push(b.sub);
    if (b.support) out.push(b.support);
    if (b.short) out.push(b.short);
    if (b.paras) out.push(...b.paras);
    if (b.list) out.push(...b.list);
    if (b.items) out.push(...b.items.flatMap((i) => [i.t || "", i.d || "", i.q || "", i.a || ""]));
    if (b.sections) out.push(...b.sections.flatMap((s) => [s.heading, ...(s.paras || []), ...(s.list || [])]));
    if (b.heading) out.push(b.heading);
    if (b.text) out.push(b.text);
    if (b.q) out.push(b.q, ...(b.paras || []));
  }
  return out.join(" ");
};

const wc = (s) => (s.trim().match(/\S+/g) || []).length;

export default function SeoAudit() {
  const navigate = useNavigate();
  const [statuses, setStatuses] = useState({});
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    adminMe().catch(() => navigate("/admin/login"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => {
    const sitemapSet = new Set(routes.map((r) => r.path));
    const inbound = {};
    for (const p of ALL) {
      for (const r of p.related || []) inbound[r.href] = (inbound[r.href] || 0) + 1;
    }
    return ALL.map((p) => ({
      path: p.path,
      type: p.pageType,
      title: p.seo.title,
      titleLen: p.seo.title.length,
      desc: p.seo.description,
      descLen: p.seo.description.length,
      h1: p.h1,
      schemas: (p.seo.schema || []).join(", "),
      related: (p.related || []).length,
      inbound: inbound[p.path] || 0,
      words: wc(collectText(p.blocks)),
      sitemap: sitemapSet.has(p.path),
    }));
  }, []);

  const checkStatuses = async () => {
    setChecking(true);
    for (const r of rows) {
      try {
        const res = await fetch(r.path, { method: "GET" });
        setStatuses((s) => ({ ...s, [r.path]: res.status }));
      } catch {
        setStatuses((s) => ({ ...s, [r.path]: "ERR" }));
      }
    }
    setChecking(false);
  };

  const dupes = useMemo(() => {
    const seen = {};
    rows.forEach((r) => { seen[r.title] = (seen[r.title] || 0) + 1; });
    return Object.entries(seen).filter(([, c]) => c > 1).map(([t]) => t);
  }, [rows]);

  return (
    <div className="min-h-screen bg-ink" data-testid="seo-audit-page">
      <header className="border-b border-line bg-surface">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <p className="font-display font-extrabold uppercase text-white">SEO Audit <span className="text-dim font-mono text-xs ml-2">{rows.length} URL</span></p>
          <div className="flex gap-4 items-center">
            <a href="/admin/leads" className="text-xs font-mono uppercase tracking-wider text-mist hover:text-white" data-testid="seo-audit-back">← Leady</a>
            <button onClick={checkStatuses} disabled={checking} className="text-xs font-mono uppercase tracking-wider bg-amber text-white px-3 py-1.5 disabled:opacity-50" data-testid="seo-audit-check-statuses">
              {checking ? "Kontroluji…" : "Ověřit HTTP statusy"}
            </button>
          </div>
        </div>
      </header>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {dupes.length > 0 && (
          <div className="mb-4 border border-red-500/40 bg-red-500/10 p-4" data-testid="seo-audit-duplicates">
            <p className="text-red-400 text-sm font-bold">Duplicitní titulky: {dupes.join(" | ")}</p>
          </div>
        )}
        <div className="overflow-x-auto border border-line">
          <table className="w-full text-left text-xs" data-testid="seo-audit-table">
            <thead>
              <tr className="bg-elevated font-mono uppercase tracking-wider text-mist">
                <th className="px-3 py-2">URL</th>
                <th className="px-3 py-2">Typ</th>
                <th className="px-3 py-2">HTTP</th>
                <th className="px-3 py-2">Title (len)</th>
                <th className="px-3 py-2">Desc (len)</th>
                <th className="px-3 py-2">H1</th>
                <th className="px-3 py-2">Schema</th>
                <th className="px-3 py-2">Links out/in</th>
                <th className="px-3 py-2">Slova</th>
                <th className="px-3 py-2">Sitemap</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {rows.map((r) => (
                <tr key={r.path} className="bg-surface hover:bg-elevated" data-testid={`seo-row-${r.path.replace(/\//g, "-") || "home"}`}>
                  <td className="px-3 py-2 text-amber font-mono whitespace-nowrap">{r.path}</td>
                  <td className="px-3 py-2 text-dim">{r.type}</td>
                  <td className="px-3 py-2 font-mono">{statuses[r.path] === 200 ? <span className="text-emerald-400">200</span> : statuses[r.path] || "—"}</td>
                  <td className={`px-3 py-2 ${r.titleLen > 65 ? "text-red-400" : "text-mist"}`} title={r.title}>{r.title.slice(0, 45)}{r.title.length > 45 ? "…" : ""} <span className="text-dim">({r.titleLen})</span></td>
                  <td className={`px-3 py-2 ${r.descLen > 165 || r.descLen < 70 ? "text-yellow-400" : "text-mist"}`} title={r.desc}>({r.descLen})</td>
                  <td className="px-3 py-2 text-mist max-w-[200px] truncate" title={r.h1}>{r.h1}</td>
                  <td className="px-3 py-2 text-dim whitespace-nowrap">{r.schemas}</td>
                  <td className="px-3 py-2 text-dim">{r.related} / {r.inbound}</td>
                  <td className={`px-3 py-2 ${r.words < 150 ? "text-yellow-400" : "text-mist"}`}>{r.words}</td>
                  <td className="px-3 py-2">{r.sitemap ? <span className="text-emerald-400">✓</span> : <span className="text-red-400">✗</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-dim mt-4">Diagnostika: žlutá = meta description mimo 70–165 znaků nebo málo slov; červená = duplicitní/příliš dlouhý title. Word count je orientační diagnostika, ne ranking metrika.</p>
      </div>
    </div>
  );
}
